import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Coins,
  Activity,
  PlusCircle,
  MinusCircle,
  Search,
  UserCircle2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { API_BASE } from '../config';

// Types
interface User {
  email: string;
  totalCoins: number;
}

interface TransactionHistory {
  _id: string; // for key
  email: string;
  transactionType: string;
  amount: number;
  coinValue: number;
  usdAmount: number;
  date: Date;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const GWCCoin: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userTransactions, setUserTransactions] = useState<TransactionHistory[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [coinManagement, setCoinManagement] = useState({ amount: 0, type: 'add' });

  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [historyPagination, setHistoryPagination] = useState<Pagination>({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  useEffect(() => {
    fetchUsers(1, pagination.limit);
  }, [searchTerm, pagination.limit]);

  const fetchUsers = async (page: number = 1, currentLimit: number = pagination.limit) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE}/api/gwc/all-users-coins?email=${searchTerm}&page=${page}&limit=${currentLimit}`);
      const { users, totalRecords, currentPage, totalPages } = response.data;
      setUsers(users);
      setPagination({
        page: currentPage,
        limit: currentLimit,
        total: totalRecords,
        totalPages: totalPages
      });
    } catch (error) {
      console.error('Error fetching users', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserTransactions = async (email: string, page: number = 1, currentLimit: number = historyPagination.limit) => {
    try {
      setIsHistoryLoading(true);
      const response = await axios.get(`${API_BASE}/api/gwc/coinsHistory/${email}?page=${page}&limit=${currentLimit}`);
      const { history, totalRecords, currentPage, totalPages } = response.data;
      setUserTransactions(history);
      setHistoryPagination({
        page: currentPage,
        limit: currentLimit,
        total: totalRecords,
        totalPages: totalPages
      });
    } catch (error) {
      console.error('Error fetching user transactions', error);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const manageCoinBalance = async () => {
    if (!selectedUser) return;
    try {
      const endpoint = coinManagement.type === 'add'
        ? `${API_BASE}/api/gwc/addCoins/${selectedUser.email}`
        : `${API_BASE}/api/gwc/deductCoins/${selectedUser.email}`;

      await axios.post(endpoint, { coinsToManage: coinManagement.amount });
      fetchUsers(pagination.page);
      fetchUserTransactions(selectedUser.email, historyPagination.page);
      setCoinManagement({ amount: 0, type: 'add' });
    } catch (error) {
      console.error('Error managing coin balance', error);
    }
  };

  const PaginationControls = ({
    state,
    onPageChange,
    onLimitChange
  }: {
    state: Pagination,
    onPageChange: (page: number) => void,
    onLimitChange?: (limit: number) => void
  }) => (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6 md:mt-8 bg-white p-4 rounded-xl shadow-sm border border-[#e0e0e0]">
      <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-6 w-full md:w-auto">
        <div className="text-sm text-[#7f8c8d]">
          Showing {state.total === 0 ? 0 : ((state.page - 1) * state.limit) + 1} - {Math.min(state.page * state.limit, state.total)} of {state.total}
        </div>
        {onLimitChange && (
          <div className="flex items-center space-x-2 sm:border-l sm:pl-6 border-[#e0e0e0]">
            <span className="text-sm text-[#7f8c8d]">Rows:</span>
            <select
              value={state.limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              className="bg-[#f9f9fc] border border-[#e0e0e0] rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-[#6a5acd]"
            >
              {[10, 25, 50, 100].map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between w-full md:w-auto space-x-2">
        <button
          onClick={() => onPageChange(state.page - 1)}
          disabled={state.page <= 1}
          className="flex-1 md:flex-none px-3 md:px-4 py-2 border border-[#e0e0e0] rounded-lg disabled:opacity-50 hover:bg-[#f9f9fc] transition-all text-sm"
        >
          Prev
        </button>
        <div className="flex items-center px-2 md:px-4 font-medium text-[#6a5acd] text-sm whitespace-nowrap">
          Page {state.page} / {state.totalPages}
        </div>
        <button
          onClick={() => onPageChange(state.page + 1)}
          disabled={state.page >= state.totalPages}
          className="flex-1 md:flex-none px-3 md:px-4 py-2 border border-[#e0e0e0] rounded-lg disabled:opacity-50 hover:bg-[#f9f9fc] transition-all text-sm"
        >
          Next
        </button>
      </div>
    </div>
  );


  return (
    <div className="min-h-screen bg-[#f4f6f9] p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 md:mb-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6">
            <h1 className="text-2xl md:text-4xl font-bold text-[#2c3e50] flex items-center">
              <Coins className="mr-3 md:mr-4 text-[#6a5acd] w-8 h-8 md:w-11 md:h-11" />
              GWC Coin Management
            </h1>
            <div className="relative w-full md:w-96">
              <input
                type="text"
                placeholder="Search Users"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 md:p-4 pl-10 md:pl-12 border-2 border-[#e0e0e0] rounded-xl
                  bg-white shadow-sm focus:outline-none focus:border-[#6a5acd]
                  transition-all duration-300 text-sm md:text-base"
              />
              <Search className="absolute left-3 md:left-4 top-3 md:top-4 text-[#6a5acd] opacity-70 w-5 h-5 md:w-6 md:h-6" />
            </div>
          </div>
        </div>

        {/* User List */}
        <div className="space-y-4 md:space-y-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-t-4 border-b-4 border-[#6a5acd]"></div>
            </div>
          ) : (
            <>
              {users.map(user => (
                <div
                  key={user.email}
                  className="bg-white rounded-xl md:rounded-2xl shadow-md md:shadow-lg overflow-hidden
                border-2 border-transparent transition-all duration-300
                hover:border-[#6a5acd]/30"
                >
                  {/* User Summary */}
                  <div
                    onClick={() => {
                      if (selectedUser?.email === user.email) {
                        setSelectedUser(null);
                      } else {
                        setSelectedUser(user);
                        fetchUserTransactions(user.email);
                      }
                    }}
                    className={`
                  p-4 md:p-6 flex items-center justify-between cursor-pointer
                  transition-all duration-300
                  ${selectedUser?.email === user.email
                        ? 'bg-[#6a5acd] text-white'
                        : 'hover:bg-[#f9f9fc] text-[#2c3e50]'}
                `}
                  >
                    <div className="flex items-center min-w-0">
                      <UserCircle2
                        className={`w-10 h-10 md:w-14 md:h-14 mr-3 md:mr-6 shrink-0
                      ${selectedUser?.email === user.email
                            ? 'text-white'
                            : 'text-[#6a5acd]'}`}
                        strokeWidth={1.5}
                      />
                      <div className="min-w-0">
                        <h3 className="text-base md:text-2xl font-semibold truncate">{user.email}</h3>
                        <p className={`
                      opacity-70 text-sm md:text-lg
                      ${selectedUser?.email === user.email
                            ? 'text-white/80'
                            : 'text-[#7f8c8d]'}
                    `}>
                          {user.totalCoins.toFixed(2)} Coins
                        </p>
                      </div>
                    </div>
                    {selectedUser?.email === user.email ? <ChevronUp className="w-6 h-6 md:w-7 md:h-7" /> : <ChevronDown className="w-6 h-6 md:w-7 md:h-7 opacity-50" />}
                  </div>

                  {/* Expanded User Details */}
                  {selectedUser?.email === user.email && (
                    <div className="bg-[#f9f9fc] p-4 md:p-8">
                      {/* Coin Management Section */}
                      <div className="mb-6 md:mb-10 bg-white rounded-xl p-4 md:p-6 shadow-md">
                        <h4 className="text-lg md:text-xl font-semibold mb-4 md:mb-6 text-[#2c3e50]">
                          Coin Management
                        </h4>
                        <div className="flex flex-col space-y-4">
                          <input
                            type="number"
                            value={coinManagement.amount}
                            onChange={(e) => setCoinManagement(prev => ({ ...prev, amount: Number(e.target.value) }))}
                            placeholder="Enter coin amount"
                            className="w-full p-3 md:p-4 border-2 border-[#e0e0e0] rounded-xl
                          focus:outline-none focus:border-[#6a5acd] text-base md:text-lg"
                          />
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3">
                            <button
                              onClick={() => setCoinManagement({ amount: coinManagement.amount, type: 'add' })}
                              className={`
                            px-4 py-2.5 md:py-3 rounded-xl flex items-center justify-center text-xs md:text-lg font-bold
                            transition-all duration-300
                            ${coinManagement.type === 'add'
                                  ? 'bg-green-500 text-white shadow-lg shadow-green-100'
                                  : 'bg-green-50 text-green-700 hover:bg-green-100'}
                          `}
                            >
                              <PlusCircle className="mr-1.5 md:mr-3 w-4 h-4 md:w-5 md:h-5" /> Add
                            </button>
                            <button
                              onClick={() => setCoinManagement({ amount: coinManagement.amount, type: 'deduct' })}
                              className={`
                            px-4 py-2.5 md:py-3 rounded-xl flex items-center justify-center text-xs md:text-lg font-bold
                            transition-all duration-300
                            ${coinManagement.type === 'deduct'
                                  ? 'bg-red-500 text-white shadow-lg shadow-red-100'
                                  : 'bg-red-50 text-red-700 hover:bg-red-100'}
                          `}
                            >
                              <MinusCircle className="mr-1.5 md:mr-3 w-4 h-4 md:w-5 md:h-5" /> Deduct
                            </button>
                            <button
                              onClick={manageCoinBalance}
                              className="col-span-2 sm:col-span-1 px-4 py-2.5 md:py-3 bg-[#6a5acd] text-white rounded-xl
                            hover:bg-opacity-90 text-xs md:text-lg font-bold transition-all duration-300 shadow-lg shadow-[#6a5acd]/20 active:scale-95"
                            >
                              Apply Changes
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Transaction History */}
                      <div className="bg-white rounded-xl p-4 md:p-6 shadow-md">
                        <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6 text-[#2c3e50] flex items-center">
                          <Activity className="mr-3 md:mr-4 text-[#6a5acd] w-5 h-5 md:w-6 md:h-6" /> Transaction History
                        </h3>
                        <div className="space-y-3 md:space-y-4">
                          {isHistoryLoading ? (
                            <div className="flex justify-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#6a5acd]"></div>
                            </div>
                          ) : (
                            <>
                              {userTransactions.length === 0 ? (
                                <div className="text-center py-8 text-[#7f8c8d] text-sm md:text-base">
                                  No transactions found for this user.
                                </div>
                              ) : (
                                userTransactions.map((transaction) => (
                                  <div
                                    key={transaction._id}
                                    className="
                                  flex justify-between items-center
                                  bg-[#f9f9fc] p-3 md:p-5 rounded-xl
                                  border border-[#e0e0e0]
                                  transition-all hover:shadow-md
                                "
                                  >
                                    <div className="flex flex-col min-w-0 pr-2">
                                      <p className="font-medium text-[#2c3e50] text-sm md:text-lg truncate">
                                        {transaction.transactionType}
                                      </p>
                                      <p className="text-[#7f8c8d] text-xs md:text-sm">
                                        {new Date(transaction.date).toLocaleString('en-GB')}
                                      </p>
                                    </div>
                                    <p className={`
                                  font-bold text-sm md:text-xl shrink-0
                                  ${transaction.amount > 0 ? 'text-green-600' : 'text-red-600'}
                                `}>
                                      {transaction.amount > 0 ? '+' : ''}{transaction.amount.toFixed(2)} Coins
                                    </p>
                                  </div>
                                ))
                              )}
                              {historyPagination.totalPages > 1 && (
                                <PaginationControls
                                  state={historyPagination}
                                  onPageChange={(page) => fetchUserTransactions(user.email, page)}
                                  onLimitChange={(limit) => {
                                    setHistoryPagination(prev => ({ ...prev, limit, page: 1 }));
                                    fetchUserTransactions(user.email, 1, limit);
                                  }}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {!isLoading && (
                <PaginationControls
                  state={pagination}
                  onPageChange={(page) => fetchUsers(page)}
                  onLimitChange={(limit) => {
                    setPagination(prev => ({ ...prev, limit, page: 1 }));
                    fetchUsers(1, limit);
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GWCCoin;